import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Zap,
  AlertCircle,
  Wallet,
  CreditCard,
  QrCode,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, description: 'GoPay, OVO, Dana, dll' },
  { id: 'va_bca', name: 'VA BCA', icon: Building2, description: 'Transfer via BCA' },
  { id: 'va_bni', name: 'VA BNI', icon: Building2, description: 'Transfer via BNI' },
  { id: 'va_mandiri', name: 'VA Mandiri', icon: Building2, description: 'Transfer via Mandiri' },
];

// ── Helper: section heading in Cinzel style ──────────────────────────────
const SectionHeading = ({ num, title }) => (
  <div className="mb-5">
    <div className="section-heading">
      <span style={{ color: 'hsl(var(--primary))' }}>✦ {num}</span>
      <span className="text-foreground/90">/</span>
      <span>{title}</span>
    </div>
    <div className="kala-divider">
      <span className="kala-divider-icon">❖</span>
    </div>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDenom, setSelectedDenom] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [userId, setUserId] = useState('');
  const [serverId, setServerId] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchProduct(); }, [slug]);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${slug}`);
      setProduct(response.data.product);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Produk tidak ditemukan');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (denom) => {
    if (user?.role === 'reseller') return denom.reseller_price;
    return denom.price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDenom) { toast.error('Pilih nominal terlebih dahulu'); return; }
    if (!selectedPayment) { toast.error('Pilih metode pembayaran'); return; }
    if (product.input_fields.includes('user_id') && !userId) { toast.error('Masukkan User ID'); return; }
    if (product.input_fields.includes('server_id') && !serverId) { toast.error('Masukkan Server ID'); return; }
    if (!email) { toast.error('Masukkan email'); return; }

    setSubmitting(true);
    try {
      const orderData = {
        product_id: product.id,
        denomination_id: selectedDenom.id,
        game_user_id: userId,
        game_server_id: serverId || null,
        email: email,
        payment_method: selectedPayment.id,
      };
      const endpoint = user ? `${API_URL}/api/orders/authenticated` : `${API_URL}/api/orders/guest`;
      const headers = user ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(endpoint, orderData, { headers });
      const order = response.data.order;
      toast.success('Persembahan berhasil dibuat!');
      navigate(`/checkout/${order.id}`, { state: { order } });
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat pesanan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div
          className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'hsl(var(--primary) / 0.6)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen pt-16 stone-texture" style={{ background: 'hsl(var(--background))' }}>

      {/* ── TALL BANNER ── */}
      <div className="relative overflow-hidden" style={{ height: '300px' }}>
        <img
          src={product.banner_image || product.image}
          alt={product.name}
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.55)' }}
        />
        {/* Gold-to-void gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, hsl(var(--background) / 0.1) 0%, hsl(var(--background) / 0.55) 50%, hsl(var(--background)) 100%)',
          }}
        />
        {/* Subtle gold top line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(to right, transparent, hsl(var(--primary) / 0.4), transparent)' }}
        />

        {/* Banner content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            className="mb-5 text-foreground/60 hover:text-primary text-xs tracking-widest"
            style={{ fontFamily: "'Space Mono', monospace" }}
            onClick={() => navigate('/')}
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            [ KEMBALI ]
          </Button>

          <div className="flex items-center gap-5">
            {/* Product icon with lotus frame */}
            <div
              className="relative flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 12px hsl(var(--primary) / 0.4))' }}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-16 h-16 md:w-20 md:h-20 rounded-sm object-cover"
                style={{ border: '2px solid hsl(var(--primary) / 0.5)' }}
              />
              <span className="absolute -top-1 -left-1 text-primary/70 text-xs">✦</span>
              <span className="absolute -bottom-1 -right-1 text-secondary/60 text-xs">✦</span>
            </div>
            <div>
              <h1
                className="font-bold text-2xl md:text-4xl text-white uppercase tracking-wider relief-text"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {product.name}
              </h1>
              <p
                className="text-primary/70 text-xs uppercase tracking-widest mt-1"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                ✦ {product.category}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT: Temple Offering Chamber ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* ── LEFT COLUMN: Form sections (7/12) ── */}
          <div className="lg:col-span-7 space-y-7">

            {/* Instructions */}
            {product.instructions && (
              <div
                className="rounded-sm p-4 relative"
                style={{
                  background: 'hsl(var(--secondary) / 0.06)',
                  border: '1px solid hsl(var(--secondary) / 0.25)',
                }}
              >
                <span className="absolute top-2 left-2 text-secondary/50 text-xs">✦</span>
                <div className="flex items-start gap-3 pl-4">
                  <AlertCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/75" style={{ fontFamily: "'Lora', serif" }}>
                    {product.instructions}
                  </p>
                </div>
              </div>
            )}

            {/* ── Section 01: User ID ── */}
            {product.input_fields.length > 0 && (
              <div
                className="stone-card rounded-sm p-6"
                style={{ position: 'relative', overflow: 'hidden' }}
              >
                <SectionHeading num="01" title="DATA TUJUAN" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.input_fields.includes('user_id') && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="userId"
                        className="text-xs text-muted-foreground uppercase tracking-widest"
                        style={{ fontFamily: "'Space Mono', monospace" }}
                      >
                        ▸ User ID
                      </Label>
                      <Input
                        id="userId"
                        type="text"
                        placeholder="Masukkan User ID"
                        className="text-white rounded-sm focus:ring-1"
                        style={{
                          background: 'hsl(var(--background) / 0.6)',
                          border: '1px solid hsl(var(--border))',
                          fontFamily: "'Space Mono', monospace",
                          fontSize: '13px',
                        }}
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        data-testid="user-id-input"
                        onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                        onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                      />
                    </div>
                  )}
                  {product.input_fields.includes('server_id') && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="serverId"
                        className="text-xs text-muted-foreground uppercase tracking-widest"
                        style={{ fontFamily: "'Space Mono', monospace" }}
                      >
                        ▸ Server ID / Zone ID
                      </Label>
                      <Input
                        id="serverId"
                        type="text"
                        placeholder="Masukkan Server ID"
                        className="text-white rounded-sm"
                        style={{
                          background: 'hsl(var(--background) / 0.6)',
                          border: '1px solid hsl(var(--border))',
                          fontFamily: "'Space Mono', monospace",
                          fontSize: '13px',
                        }}
                        value={serverId}
                        onChange={(e) => setServerId(e.target.value)}
                        data-testid="server-id-input"
                        onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                        onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Section 02: Denomination ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading
                num={product.input_fields.length > 0 ? '02' : '01'}
                title="PILIH PERSEMBAHAN"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {product.denominations.map((denom) => (
                  <button
                    key={denom.id}
                    className={`denom-card p-4 rounded-sm text-left flex items-center justify-between ${selectedDenom?.id === denom.id ? 'selected' : ''}`}
                    onClick={() => setSelectedDenom(denom)}
                    data-testid={`denom-${denom.id}`}
                  >
                    <div>
                      <p
                        className="font-bold text-white text-sm"
                        style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.04em' }}
                      >
                        {denom.name}
                      </p>
                      {user?.role === 'reseller' && denom.price !== denom.reseller_price && (
                        <p className="text-xs text-muted-foreground line-through mt-0.5" style={{ fontFamily: "'Space Mono', monospace" }}>
                          {formatPrice(denom.price)}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p
                        className="text-sm font-bold"
                        style={{ color: 'hsl(var(--primary))', fontFamily: "'Space Mono', monospace" }}
                      >
                        {formatPrice(getPrice(denom))}
                      </p>
                      {selectedDenom?.id === denom.id && (
                        <span className="text-primary text-xs">✦</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Section 03: Payment ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading
                num={product.input_fields.length > 0 ? '03' : '02'}
                title="JALUR PEMBAYARAN"
              />
              <div className="grid grid-cols-2 gap-3">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      className={`payment-card p-3 rounded-sm flex items-center gap-3 ${selectedPayment?.id === method.id ? 'selected' : ''}`}
                      onClick={() => setSelectedPayment(method)}
                      data-testid={`payment-${method.id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0"
                        style={{
                          background: 'hsl(var(--secondary) / 0.08)',
                          border: '1px solid hsl(var(--secondary) / 0.2)',
                        }}
                      >
                        <Icon className="w-4 h-4" style={{ color: 'hsl(var(--secondary))' }} />
                      </div>
                      <div className="text-left">
                        <p
                          className="font-bold text-white text-xs"
                          style={{ fontFamily: "'Cinzel', serif" }}
                        >
                          {method.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                          {method.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Section 04: Email ── */}
            <div className="stone-card rounded-sm p-6">
              <SectionHeading
                num={product.input_fields.length > 0 ? '04' : '03'}
                title="EMAIL KONFIRMASI"
              />
              <div className="space-y-2">
                <Label
                  htmlFor="email"
                  className="text-xs text-muted-foreground uppercase tracking-widest"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  ▸ Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="candi@voltagex.id"
                  className="text-white rounded-sm"
                  style={{
                    background: 'hsl(var(--background) / 0.6)',
                    border: '1px solid hsl(var(--border))',
                    fontFamily: "'Lora', serif",
                  }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  onFocus={(e) => e.target.style.borderColor = 'hsl(var(--primary) / 0.6)'}
                  onBlur={(e) => e.target.style.borderColor = 'hsl(var(--border))'}
                />
                <p className="text-[11px] text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                  Bukti transaksi akan dikirim ke alamat email ini
                </p>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Order Summary (5/12) ── */}
          <div className="lg:col-span-5">
            <div
              className="sticky top-24 rounded-sm overflow-hidden"
              style={{
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--primary) / 0.2)',
                boxShadow: '0 0 40px hsl(var(--primary) / 0.04)',
              }}
            >
              {/* Header */}
              <div
                className="px-6 py-4"
                style={{
                  background: 'hsl(var(--primary) / 0.06)',
                  borderBottom: '1px solid hsl(var(--primary) / 0.15)',
                }}
              >
                <h2
                  className="text-sm text-primary uppercase tracking-widest"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  ✦ Ringkasan Persembahan
                </h2>
              </div>

              <div className="p-6">
                {/* Product preview */}
                <div
                  className="flex items-center gap-3 pb-4 mb-4"
                  style={{ borderBottom: '1px solid hsl(var(--border))' }}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-12 h-12 rounded-sm object-cover"
                    style={{ border: '1px solid hsl(var(--border))' }}
                  />
                  <div>
                    <p
                      className="font-bold text-white text-xs uppercase tracking-wide"
                      style={{ fontFamily: "'Cinzel', serif" }}
                    >
                      {product.name}
                    </p>
                    {selectedDenom && (
                      <p className="text-xs text-primary mt-0.5" style={{ fontFamily: "'Space Mono', monospace" }}>
                        {selectedDenom.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-4">
                  {(userId || serverId) && (
                    <div
                      className="rounded-sm p-3"
                      style={{ background: 'hsl(var(--background) / 0.5)', border: '1px solid hsl(var(--border))' }}
                    >
                      {userId && (
                        <p className="text-xs text-foreground/70" style={{ fontFamily: "'Space Mono', monospace" }}>
                          TARGET ID: <span className="text-white font-bold">{userId}</span>
                        </p>
                      )}
                      {serverId && (
                        <p className="text-xs text-foreground/70 mt-1" style={{ fontFamily: "'Space Mono', monospace" }}>
                          SERVER ID: <span className="text-white font-bold">{serverId}</span>
                        </p>
                      )}
                    </div>
                  )}
                  {selectedPayment && (
                    <div className="flex items-center gap-2 text-xs text-foreground/60">
                      <Wallet className="w-3 h-3" style={{ color: 'hsl(var(--secondary))' }} />
                      <span style={{ fontFamily: "'Space Mono', monospace" }}>
                        {selectedPayment.name.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div
                  className="flex justify-between items-center pt-4 mb-6"
                  style={{ borderTop: '1px solid hsl(var(--border))' }}
                >
                  <span
                    className="text-xs text-muted-foreground uppercase tracking-wider"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    Total Emas
                  </span>
                  <span
                    className="text-xl font-bold text-glow-gold"
                    style={{ color: 'hsl(var(--primary))', fontFamily: "'Space Mono', monospace" }}
                  >
                    {selectedDenom ? formatPrice(getPrice(selectedDenom)) : 'Rp 0'}
                  </span>
                </div>

                {/* CTA */}
                <Button
                  className="w-full stone-btn font-bold uppercase tracking-widest py-6 text-background text-sm"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: (!selectedDenom || !selectedPayment)
                      ? 'hsl(var(--muted))'
                      : 'linear-gradient(135deg, #C9A227, #A07B1E)',
                    border: 'none',
                    letterSpacing: '0.06em',
                    cursor: (!selectedDenom || !selectedPayment) ? 'not-allowed' : 'pointer',
                    color: (!selectedDenom || !selectedPayment) ? 'hsl(var(--muted-foreground))' : 'hsl(var(--background))',
                  }}
                  onClick={handleSubmit}
                  disabled={submitting || !selectedDenom || !selectedPayment}
                  data-testid="order-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                        style={{ borderColor: 'rgba(0,0,0,0.3)', borderTopColor: 'transparent' }}
                      />
                      Memproses...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      ✦ PERSEMBAHKAN
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
